import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { cn } from '@/lib/utils';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    // UNDERLINE-tabs (best practice fő-nézet-váltáshoz — GitHub/Stripe/Material): a tab-sor alatt egy halvány,
    // teljes-szélességű alapvonal (a lista-hairline színnel), az aktív tab egy vastagabb accent-aláhúzást kap.
    className={cn(
      'inline-flex items-center gap-5 border-b border-[color:var(--table-hairline)]',
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    // Inaktív: másodlagos (muted) szöveg, hoverre foreground. Aktív: ZÖLD (brand) szöveg + 2px zöld aláhúzás,
    // ami a `-mb-px`-szel az alapvonalra ül. A `border-b-2 border-transparent` rezervál helyet (nincs ugrás).
    className={cn(
      'text-muted-foreground relative -mb-px inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-1 pb-2.5 pt-1.5 text-sm font-medium transition-colors [&_svg]:size-4 [&_svg]:shrink-0',
      'hover:text-foreground',
      // Aktív = az aktív sidebar-menüpont zöldje (accent-600 light / accent-400 dark) — szöveg + 2px aláhúzás.
      'data-[state=active]:border-accent-600 data-[state=active]:text-accent-600 dark:data-[state=active]:border-accent-400 dark:data-[state=active]:text-accent-400',
      'focus-visible:ring-ring focus:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50',
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

/**
 * DARABSZÁM-PLAKETT a fül feliratában.
 *
 * MÉRT HIBA (user-lelet): a plakettet a hívók kézzel rajzolták, `text-[11px]`-szel — a Tailwind arbitrary
 * betűméret viszont a SOR-MAGASSÁGOT nem viszi magával, tehát a 20 px-es örökölt sor + 2×2 px padding miatt a
 * plakettes fül 4 px-szel MAGASABB lett a plakett nélkülinél. A fül-sáv alapvonala a legmagasabb fülhöz
 * igazodik, így az AKTÍV, plakett nélküli fül zöld aláhúzása a szürke vonal FÖLÖTT lebegett. A `leading-none`
 * ezt zárja le: a plakett nem nyújtja a sor-dobozt.
 *
 * Közös komponens, mert két lista-oldal (jegyek · számlák) ugyanezt a plakettet rajzolta — és pont az egyik
 * csúszott el.
 */
function TabsCount({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="bg-muted text-muted-foreground ml-1.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums leading-none">
      {children}
    </span>
  );
}

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'focus-visible:ring-ring mt-2 focus-visible:outline-none focus-visible:ring-1',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsCount, TabsList, TabsTrigger, TabsContent };
